import {
  Anchor,
  Blockquote,
  Code,
  Divider,
  List,
  Stack,
} from "@mantine/core";
import { Link } from "gatsby";
import React, { useCallback, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Text, Title } from "@components";
import { PromptCard } from "@components/PromptCard/PromptCard.component";
import { copyToClipboard } from "../../utils/copyToClipboard";
import { slugify } from "../../utils/slugify";

import * as styles from "./DocsMarkdown.module.css";

interface DocsMarkdownProps {
  content: string;
  /** Maps markdown filenames to route slugs for internal link resolution */
  linkMap?: Record<string, string>;
  /** Base URL path for this docs section, e.g. "/bob-builder" */
  basePath?: string;
  /** Detect if a code block should render as a PromptCard */
  isPromptContent?: (text: string) => boolean;
}

function transformHref(
  href: string | undefined,
  linkMap: Record<string, string> | undefined,
  basePath: string
): { to?: string; href?: string; isInternal: boolean } {
  if (!href) return { href: "#", isInternal: false };

  const filename = href.replace(/^\.?\/?/, "");
  const slug = linkMap?.[filename];
  if (slug) {
    return {
      to: slug === "home" ? `${basePath}/` : `${basePath}/${slug}`,
      isInternal: true,
    };
  }

  if (href.startsWith("#")) {
    return { href, isInternal: false };
  }

  return { href, isInternal: false };
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    copyToClipboard(text).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <button
      className={`${styles.copyButton} ${copied ? styles.copyButtonCopied : ""}`}
      onClick={handleCopy}
      type="button"
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

function extractTextFromChildren(children: React.ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(extractTextFromChildren).join("");
  if (React.isValidElement(children) && children.props?.children) {
    return extractTextFromChildren(children.props.children);
  }
  return "";
}

export function DocsMarkdown({
  content,
  linkMap,
  basePath = "/",
  isPromptContent,
}: DocsMarkdownProps) {
  const codeBlockRef = useRef<string>("");

  return (
    <Stack gap="lg">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => {
            const text = extractTextFromChildren(children);
            return (
              <Title order={1} mt="xxl" id={slugify(text)}>
                {children}
              </Title>
            );
          },
          h2: ({ children }) => {
            const text = extractTextFromChildren(children);
            return (
              <Title order={2} mt="xl" id={slugify(text)}>
                {children}
              </Title>
            );
          },
          h3: ({ children }) => {
            const text = extractTextFromChildren(children);
            return (
              <Title order={3} mt="xl" id={slugify(text)}>
                {children}
              </Title>
            );
          },
          h4: ({ children }) => {
            const text = extractTextFromChildren(children);
            return (
              <Title order={4} mt="xl" id={slugify(text)}>
                {children}
              </Title>
            );
          },
          h5: ({ children }) => {
            const text = extractTextFromChildren(children);
            return (
              <Title order={5} mt="xl" id={slugify(text)}>
                {children}
              </Title>
            );
          },
          h6: ({ children }) => {
            const text = extractTextFromChildren(children);
            return (
              <Title order={6} mt="xl" id={slugify(text)}>
                {children}
              </Title>
            );
          },
          p: ({ children }) => <Text>{children}</Text>,
          a: ({ href, children }) => {
            const link = transformHref(href, linkMap, basePath);
            if (link.isInternal && link.to) {
              return (
                <Anchor component={Link} to={link.to} td="underline">
                  {children}
                </Anchor>
              );
            }
            return (
              <Anchor
                href={link.href}
                target={link.href?.startsWith("#") ? undefined : "_blank"}
                rel={link.href?.startsWith("#") ? undefined : "noopener noreferrer"}
                td="underline"
              >
                {children}
              </Anchor>
            );
          },
          strong: ({ children }) => (
            <Text component="span" fw="600">
              {children}
            </Text>
          ),
          em: ({ children }) => (
            <Text component="span" fs="italic">
              {children}
            </Text>
          ),
          code: ({ children, className }) => {
            // Inline code (no className means not inside a fenced block)
            if (!className) {
              return (
                <Code c="var(--mantine-color-fontDarkSecondary)">
                  {children}
                </Code>
              );
            }
            // Fenced code block content — store for the pre wrapper
            const text = extractTextFromChildren(children);
            codeBlockRef.current = text;
            return <code className={className}>{children}</code>;
          },
          pre: ({ children }) => {
            const text = codeBlockRef.current;

            if (isPromptContent?.(text)) {
              return <PromptCard text={text} />;
            }

            return (
              <div className={styles.codeBlockWrapper}>
                <CopyButton text={text} />
                <Code block my="md">
                  {children}
                </Code>
              </div>
            );
          },
          blockquote: ({ children }) => (
            <Blockquote color="gray" my="md">
              {children}
            </Blockquote>
          ),
          hr: () => <Divider my="lg" />,
          ul: ({ children, node }) => {
            const ulClass = (node as { properties?: { className?: unknown } })?.properties?.className;
            const classes = Array.isArray(ulClass) ? ulClass : [ulClass];
            if (classes.includes("contains-task-list")) {
              return <ul className={styles.taskList}>{children}</ul>;
            }
            return <List spacing="sm" my="md">{children}</List>;
          },
          ol: ({ children }) => (
            <List spacing="sm" my="md" type="ordered">
              {children}
            </List>
          ),
          li: ({ children, node }) => {
            const liClass = (node as { properties?: { className?: unknown } })?.properties?.className;
            const classes = Array.isArray(liClass) ? liClass : [liClass];
            if (classes.includes("task-list-item")) {
              return <li className={styles.taskItem}>{children}</li>;
            }
            return <List.Item>{children}</List.Item>;
          },
          input: ({ type, checked }) => {
            if (type === "checkbox") {
              return (
                <input
                  type="checkbox"
                  checked={checked ?? false}
                  readOnly
                  className={styles.taskCheckbox}
                />
              );
            }
            return null;
          },
          table: ({ children }) => (
            <div className={styles.tableWrapper}>
              <table>{children}</table>
            </div>
          ),
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt || ""}
              style={{
                maxWidth: "100%",
                borderRadius: "var(--mantine-radius-md)",
                marginTop: "var(--mantine-spacing-md)",
                marginBottom: "var(--mantine-spacing-md)",
              }}
            />
          ),
          del: ({ children }) => (
            <Text component="span" td="line-through">
              {children}
            </Text>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </Stack>
  );
}
